import { Component, ReactNode, ErrorInfo } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { AlertTriangle, RefreshCw, Home } from "lucide-react";
import { useT } from "@/lib/i18n";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
}

interface FallbackProps {
  error?: Error;
  errorInfo?: ErrorInfo;
  onRetry: () => void;
  onHome: () => void;
}

function ErrorFallback({ error, errorInfo, onRetry, onHome }: FallbackProps) {
  const t = useT();
  return (
    <div className="min-h-full w-full flex items-center justify-center p-4">
      <Card className="border-2 border-red-200 bg-red-50 max-w-lg">
        <CardContent className="p-6 text-center space-y-4">
          <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
            <AlertTriangle className="w-8 h-8 text-red-500" />
          </div>

          <div className="space-y-2">
            <h2 className="text-xl font-semibold text-red-800 select-none">{t("addin-boundary-title")}</h2>
            <p className="text-red-700 text-sm leading-tight select-none">{t("addin-boundary-text")}</p>
          </div>

          <div className="space-y-2">
            <Button onClick={onRetry} className="w-full" size="sm">
              <RefreshCw className="w-4 h-4 mr-2" />
              {t("addin-boundary-retry")}
            </Button>
            <Button onClick={onHome} variant="outline" className="w-full" size="sm">
              <Home className="w-4 h-4 mr-2" />
              {t("addin-boundary-home")}
            </Button>
          </div>

          {process.env.NODE_ENV === "development" && error && (
            <details className="text-left text-wrap">
              <summary className="cursor-pointer text-sm font-medium text-red-800 select-none">
                {t("addin-boundary-details")}
              </summary>
              <pre className="mt-2 text-xs text-red-600 bg-red-100 p-2 rounded text-wrap break-all max-h-32 overflow-y-auto">
                {error.toString()}
                {errorInfo?.componentStack}
              </pre>
            </details>
          )}

          <div className="text-xs text-red-500 select-none">{t("addin-boundary-support")}</div>
        </CardContent>
      </Card>
    </div>
  );
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log the error details
    console.error("React Error Boundary caught an error:", error, errorInfo);

    this.setState({
      error,
      errorInfo,
    });
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: undefined, errorInfo: undefined });
  };

  handleGoHome = () => {
    // Force a full page reload to reset the app state
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <ErrorFallback
          error={this.state.error}
          errorInfo={this.state.errorInfo}
          onRetry={this.handleRetry}
          onHome={this.handleGoHome}
        />
      );
    }

    return this.props.children;
  }
}
